import React, { useCallback, memo, useMemo } from 'react';
import {
  Box,
  Card,
  CardBody,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  CardHeader,
  Text,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaChartPie } from 'react-icons/fa';
import LocationDropDown from '../../utility/location';
import CustomDateRangePicker from '../../utility/dateRangePicker';
import ChartRender from './chart';
import ConsolidatedBarChart from './consolidatedBarChart';
import { useFeatureFlagFromContext } from '../../../launchdarkly';
import { ActionMenu, IconButton, useNexus } from '@spotoninc/nexus-react';
import { DownloadIcon } from '@spotoninc/nexus-web-assets';
import { toPng } from 'html-to-image';
import '../../../styles/reskinned-card-header.css';
import '../../../styles/action-menu-fix.css';
import { html2canvas } from 'html2canvas';

// Extracted download utility functions to avoid recreating them on each render
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up to avoid memory leaks
};

const downloadSVG = svgElement => {
  if (!svgElement) return;

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], {
    type: 'image/svg+xml;charset=utf-8',
  });
  const svgUrl = URL.createObjectURL(svgBlob);
  const downloadLink = document.createElement('a');
  downloadLink.href = svgUrl;
  downloadLink.download = 'financial_chart.svg';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(svgUrl); // Clean up to avoid memory leaks
};

// Memoized component for the original card header
const OriginalCardHeader = memo(
  ({ locationValue, setLocation, dateValue, value }) => (
    <CardHeader height={{ base: '150px', sm: '100px', md: '70px', lg: '70px' }}>
      <Flex
        gap={2}
        height="100%"
        direction={{ base: 'column', sm: 'row' }}
        justifyContent={{
          base: 'space-between',
          sm: 'space-around',
          md: 'space-between',
        }}
      >
        <Flex gap={2} alignItems={'center'}>
          <Icon as={FaChartPie} />
          <Text fontSize={'md'}>Overview</Text>
        </Flex>
        <Flex
          gap={2}
          direction={{ base: 'column', sm: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Flex
            flex={1}
            minWidth={{ base: '280px', sm: '280px', md: '250px' }}
            maxWidth={{ base: '280px', sm: '280px', md: '250px' }}
          >
            <LocationDropDown
              locationValue={locationValue}
              setLocation={setLocation}
            />
          </Flex>
          <Flex
            flex={1}
            fontSize={'sm'}
            width={'100%'}
            justifyContent={{
              base: 'center',
              sm: 'space-between',
              md: 'space-between',
            }}
          >
            <div className="date-picker-limited-width">
              <CustomDateRangePicker dateValue={dateValue} value={value} />
            </div>
          </Flex>
        </Flex>
      </Flex>
    </CardHeader>
  )
);

// Memoized component for the Nexus card header
const NexusCardHeader = memo(
  ({ locationValue, setLocation, dateValue, value, onDownload }) => {
    // Get Nexus theme tokens for consistent styling
    const {
      theme: { tokens },
    } = useNexus();

    return (
      <CardHeader className="reskinned-card-header">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', md: 'center' }}
          width="100%"
        >
          {/* Top row on mobile: Title and Download Button */}
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            mb={{ base: 2, md: 0 }}
          >
            <Text
              style={{
                color: tokens.semanticColorTextNeutralStrongestNone,
                fontFamily: tokens.primitiveFontFamilySans,
                fontSize: tokens.primitiveFontSize300,
                fontWeight: tokens.primitiveFontWeightSemibold,
                lineHeight: tokens.primitiveFontLineHeight200,
                letterSpacing: tokens.primitiveFontLetterSpacingDefault,
                verticalAlign: 'middle',
              }}
              className="financial-summary-title"
              whiteSpace="nowrap"
            >
              Financial Summary
            </Text>

            {/* Download Button with ActionMenu - on same line as title on mobile */}
            <Box display={{ base: 'block', md: 'none' }}>
              <ActionMenu
                className="chart-download-menu"
                options={[
                  {
                    label: 'Download as SVG',
                    onClick: () => onDownload('SVG'),
                  },
                  {
                    label: 'Download as PNG',
                    onClick: () => onDownload('PNG'),
                  },
                  {
                    label: 'Download as CSV',
                    onClick: () => onDownload('CSV'),
                    hasDivider: true,
                  },
                ]}
              >
                {({ getRootProps }) => (
                  <IconButton
                    {...getRootProps()}
                    icon={DownloadIcon}
                    label="Download"
                    variant="tonal"
                    size="small"
                  />
                )}
              </ActionMenu>
            </Box>
          </Flex>

          {/* Middle section: Date Picker on mobile, Date Picker + Download on desktop */}
          <Flex
            width={{ base: '100%', md: 'auto' }}
            justifyContent={{ base: 'center', md: 'flex-end' }}
            alignItems="center"
          >
            {/* Date Range Picker */}
            <div className="date-picker-limited-width">
              <CustomDateRangePicker dateValue={dateValue} value={value} />
            </div>

            {/* Download Button with ActionMenu - only visible on desktop */}
            <Box display={{ base: 'none', md: 'block' }} ml={5} flexShrink={0}>
              <ActionMenu
                className="chart-download-menu"
                options={[
                  {
                    label: 'Download as SVG',
                    onClick: () => onDownload('SVG'),
                  },
                  {
                    label: 'Download as PNG',
                    onClick: () => onDownload('PNG'),
                  },
                  {
                    label: 'Download as CSV',
                    onClick: () => onDownload('CSV'),
                    hasDivider: true,
                  },
                ]}
              >
                {({ getRootProps }) => (
                  <IconButton
                    {...getRootProps()}
                    icon={DownloadIcon}
                    label="Download"
                    variant="tonal"
                    size="small"
                  />
                )}
              </ActionMenu>
            </Box>
          </Flex>
        </Flex>
      </CardHeader>
    );
  }
);

// Memoized TabPanel components
const ProfitTabPanel = memo(({ income }) => (
  <TabPanel>
    <ChartRender
      type="bar"
      data={income.data}
      series={income.series}
      categories={income.categories}
      id="Profit"
    />
  </TabPanel>
));

const ExpenseTabPanel = memo(({ expense }) => (
  <TabPanel>
    <ChartRender
      type="bar"
      data={expense.data}
      series={expense.series}
      categories={expense.categories}
      id="Expense"
    />
  </TabPanel>
));

const RevenueTabPanel = memo(({ revenue }) => (
  <TabPanel>
    <ChartRender
      type="bar"
      data={revenue.data}
      series={revenue.series}
      categories={revenue.categories}
      id="Revenue"
    />
  </TabPanel>
));

// Main TabChart component
const TabChart = memo(props => {
  // Use the context-based hook to access feature flags
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // Destructure props to avoid repeated access
  const {
    income,
    expense: expenseParams,
    revenue: revenueParams,
    locationValue,
    setLocation,
    dateValue,
    value,
  } = props;

  // Removed console.log statements to prevent unnecessary processing

  // Memoize categories to avoid recalculation
  const categories = useMemo(
    () =>
      income?.categories ||
      expenseParams?.categories ||
      revenueParams?.categories ||
      [],
    [income?.categories, expenseParams?.categories, revenueParams?.categories]
  );

  const expense = useMemo(() => {
    if (categories.length === 0) return expenseParams;
    // function that maps categories to expense category and if not found then set 0 to that data
    const updatedData = categories.map(category => {
      const matchIndex = expenseParams?.categories?.indexOf(category) ?? -1;
      if (
        matchIndex > -1 &&
        Array.isArray(expenseParams.data) &&
        matchIndex < expenseParams.data.length
      ) {
        return expenseParams?.data?.[matchIndex] ?? 0;
      } else {
        return 0;
      }
    });
    return {
      ...expenseParams,
      categories,
      data: updatedData,
    };
  }, [categories, expenseParams]);

  const revenue = useMemo(() => {
    if (categories.length === 0) return revenueParams;
    // function that maps categories to revenue category and if not found then set 0 to that data
    const updatedData = categories.map(category => {
      const matchIndex = revenueParams?.categories?.indexOf(category) ?? -1;
      if (
        matchIndex > -1 &&
        Array.isArray(revenueParams.data) &&
        matchIndex < revenueParams.data.length
      ) {
        return revenueParams?.data?.[matchIndex] ?? 0;
      } else {
        return 0;
      }
    });
    return {
      ...revenueParams,
      categories,
      data: updatedData,
    };
  }, [categories, revenueParams]);

  // Function to handle CSV download
  const handleCSVDownload = useCallback(
    specificChart => {
      // Only compute headers and rows when needed
      const headers = ['Category'];
      const rows = [];

      if (!specificChart || specificChart === 'Profit') {
        headers.push('Profit');
      }

      if (!specificChart || specificChart === 'Revenue') {
        headers.push('Revenue');
      }

      if (!specificChart || specificChart === 'Expense') {
        headers.push('Expense');
      }

      // Prepare data rows
      categories.forEach((category, index) => {
        const row = [category];

        if (!specificChart || specificChart === 'Profit') {
          row.push(income?.data?.[index] || 0);
        }

        if (!specificChart || specificChart === 'Revenue') {
          row.push(revenue?.data?.[index] || 0);
        }

        if (!specificChart || specificChart === 'Expense') {
          row.push(expense?.data?.[index] || 0);
        }

        rows.push(row);
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      // Download the CSV
      downloadCSV(
        csvContent,
        specificChart ? `${specificChart}_Data.csv` : 'Financial_Summary.csv'
      );
    },
    [categories, income?.data, expense?.data, revenue?.data]
  );

  // Function to handle SVG/PNG download
  const handleImageDownload = useCallback(
    format => {
      const chartElement =
        document.querySelector('.consolidated-bar-chart') ||
        document.getElementById(
          format === 'Profit'
            ? 'Profit'
            : format === 'Expense'
              ? 'Expense'
              : 'Revenue'
        );

      if (!chartElement) {
        console.error('Chart element not found');
        return;
      }

      if (format === 'SVG') {
        if (showReskin) {
          // Simplified approach: Create an SVG with just the chart and manually add legend text
          try {
            // Get the chart dimensions
            const chartRect = chartElement.getBoundingClientRect();
            const width = chartRect.width;
            const height = chartRect.height;

            // Create a new SVG document
            const svgNS = 'http://www.w3.org/2000/svg';
            const newSvg = document.createElementNS(svgNS, 'svg');
            newSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            newSvg.setAttribute('width', width);
            newSvg.setAttribute('height', height);
            newSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);

            // First, add the chart background
            const background = document.createElementNS(svgNS, 'rect');
            background.setAttribute('width', width);
            background.setAttribute('height', height);
            background.setAttribute('fill', 'white');
            newSvg.appendChild(background);

            // Get the original SVG from the chart
            const originalSvg = chartElement.querySelector('svg');
            if (!originalSvg) {
              throw new Error('SVG element not found');
            }

            // Find and remove any existing text elements in the legend area
            // to prevent duplication
            const legendGroup = originalSvg.querySelector('.highcharts-legend');
            if (legendGroup) {
              const existingTexts = legendGroup.querySelectorAll('text');
              existingTexts.forEach(text => text.remove());
            }

            // Clone the original SVG content
            const svgContent = originalSvg.cloneNode(true);

            // Append all child nodes from the original SVG
            while (svgContent.firstChild) {
              newSvg.appendChild(svgContent.firstChild);
            }

            // Now add the legend text elements
            const legendItems = chartElement.querySelectorAll(
              '.highcharts-legend-item'
            );
            legendItems.forEach(item => {
              const textSpan = item.querySelector('span[data-z-index]');
              if (textSpan) {
                const textContent = textSpan.textContent;
                const itemRect = item.getBoundingClientRect();
                const relativeX = itemRect.left - chartRect.left + 30;
                const relativeY = itemRect.top - chartRect.top + 15;

                const textElement = document.createElementNS(svgNS, 'text');
                textElement.textContent = textContent;
                textElement.setAttribute('x', relativeX);
                textElement.setAttribute('y', relativeY);
                textElement.setAttribute(
                  'style',
                  'font-family: Poppins, sans-serif; font-size: 12px; font-weight: 500; fill: rgb(10, 9, 27);'
                );

                newSvg.appendChild(textElement);
              }
            });

            // Download the SVG
            downloadSVG(newSvg);
            return;
          } catch (error) {
            console.error('Error with simplified approach:', error);
            // Fall back to PNG approach
            fallbackToPNG();
          }

          // PNG fallback function
          function fallbackToPNG() {
            try {
              // Use html2canvas to create a PNG
              if (typeof html2canvas === 'function') {
                html2canvas(chartElement, {
                  backgroundColor: 'white',
                  scale: 2,
                  logging: false,
                  useCORS: true,
                })
                  .then(canvas => {
                    // Convert to PNG and download
                    const link = document.createElement('a');
                    link.download = 'financial_chart.png';
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                  })
                  .catch(err => {
                    console.error('html2canvas PNG fallback failed:', err);
                    // Final fallback to toPng
                    toPng(chartElement)
                      .then(dataUrl => {
                        const link = document.createElement('a');
                        link.download = 'financial_chart.png';
                        link.href = dataUrl;
                        link.click();
                      })
                      .catch(err => {
                        console.error('Final PNG fallback failed:', err);
                      });
                  });
              } else {
                // Use toPng directly if html2canvas is not available
                toPng(chartElement)
                  .then(dataUrl => {
                    const link = document.createElement('a');
                    link.download = 'financial_chart.png';
                    link.href = dataUrl;
                    link.click();
                  })
                  .catch(err => {
                    console.error('toPng fallback failed:', err);
                  });
              }
            } catch (error) {
              console.error('All PNG fallbacks failed:', error);
              // Last resort: just try to get any SVG we can
              const svgElement = chartElement.querySelector('svg');
              if (svgElement) {
                downloadSVG(svgElement);
              }
            }
          }
        } else {
          // Original behavior for non-reskinned version
          const svgElement = chartElement.querySelector('svg');
          downloadSVG(svgElement);
        }
      } else if (format === 'PNG') {
        // Convert to PNG
        toPng(chartElement)
          .then(dataUrl => {
            const link = document.createElement('a');
            link.download = 'financial_chart.png';
            link.href = dataUrl;
            link.click();
          })
          .catch(err => {
            console.error('Error generating PNG:', err);
          });
      } else if (format === 'CSV') {
        // Generate CSV from chart data
        handleCSVDownload();
      }
    },
    [handleCSVDownload, showReskin]
  );

  // Render the appropriate card header based on the feature flag
  const headerComponent = useMemo(() => {
    return showReskin ? (
      <NexusCardHeader
        locationValue={locationValue}
        setLocation={setLocation}
        dateValue={dateValue}
        value={value}
        onDownload={handleImageDownload}
      />
    ) : (
      <OriginalCardHeader
        locationValue={locationValue}
        setLocation={setLocation}
        dateValue={dateValue}
        value={value}
      />
    );
  }, [
    showReskin,
    locationValue,
    setLocation,
    dateValue,
    value,
    handleImageDownload,
  ]);

  // Memoize the chart data to prevent unnecessary re-renders
  // We're using a custom equality check by wrapping the data in useMemo
  // This ensures the references only change when the actual data changes
  const memoizedIncome = useMemo(() => income, [income]);
  const memoizedExpense = useMemo(() => expense, [expense]);
  const memoizedRevenue = useMemo(() => revenue, [revenue]);

  // Memoize the chart content to prevent unnecessary re-renders
  const chartContent = useMemo(() => {
    if (showReskin) {
      return (
        <ConsolidatedBarChart
          income={memoizedIncome}
          expense={memoizedExpense}
          revenue={memoizedRevenue}
          categories={categories}
        />
      );
    } else {
      return (
        <Tabs isLazy={true}>
          <TabList>
            <Tab>Profit</Tab>
            <Tab>Expense</Tab>
            <Tab>Revenue</Tab>
          </TabList>
          <TabPanels>
            <ProfitTabPanel income={memoizedIncome} />
            <ExpenseTabPanel expense={memoizedExpense} />
            <RevenueTabPanel revenue={memoizedRevenue} />
          </TabPanels>
        </Tabs>
      );
    }
  }, [
    showReskin,
    memoizedIncome,
    memoizedExpense,
    memoizedRevenue,
    categories,
  ]);

  // Render the final component
  return (
    <Card width={'100%'} p={1}>
      {headerComponent}
      {showReskin ? null : <Divider mt={0} />}
      <CardBody>{chartContent}</CardBody>
    </Card>
  );
});

// Export the memoized function component
export default TabChart;
